import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState(null);
  const [currentLang, setCurrentLang] = useState(() => {
    // Получаем язык из localStorage или браузера
    const savedLang = localStorage.getItem('userLang');
    if (savedLang && ['en', 'ru'].includes(savedLang)) {
      return savedLang;
    }
    // Определяем язык браузера
    const browserLang = navigator.language.split('-')[0];
    return ['en', 'ru'].includes(browserLang) ? browserLang : 'en';
  });

  const API_URL = process.env.REACT_APP_API;

  useEffect(() => {
    // Получаем CSRF токен при загрузке приложения
    const initializeApp = async () => {
      try {
        await getCsrfToken();
      } catch (error) {
        // Ошибка получения CSRF токена
      }
      
      // Очищаем невалидный токен, если он есть
      clearInvalidToken();
      
      // Проверяем токен при загрузке приложения
      if (token) {
        await validateToken();
      } else {
        // Принудительно сбрасываем состояние при отсутствии токена
        setUser(null);
        setToken(null);
        setLoading(false);
      }
    };
    
    initializeApp();
  }, []);

  const validateToken = async () => {
    try {
      // Проверяем JWT токен на клиенте
      if (!token) {
        setLoading(false);
        return;
      }

      // Декодируем JWT токен
      const parts = token.split('.');
      if (parts.length !== 3) {
        logout();
        setLoading(false);
        return;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp < currentTime) {
        // Токен истек
        logout();
        setLoading(false);
        return;
      }

      // Если токен валиден, получаем актуальную информацию о пользователе
      try {
        const userInfo = await getUserInfo(token);
        setUser(userInfo);
      } catch (error) {
        // Если не удалось получить информацию, используем данные из токена
        setUser({ 
          email: payload.email || 'user@example.com',
          role: payload.role === 'admin' ? 'admin' : 'user'
        });
      }
      
      setLoading(false);
    } catch (error) {
      logout();
      setLoading(false);
    }
  };

  const getCsrfToken = async () => {
    const response = await fetch(`${API_URL}${currentLang}/csrf-token`, {
      credentials: 'include',
    });
    if (!response.ok) throw new Error('Ошибка получения CSRF токена');
    const data = await response.json();
    setCsrfToken(data.token);
    return data.token;
  };

  const refreshToken = async () => {
    try {
      const response = await fetch(`${API_URL}${currentLang}/refresh`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok && data.success && data.access_token) {
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        return data.access_token;
      } else {
        logout();
        throw new Error('Не удалось обновить токен');
      }
    } catch (error) {
      logout();
      throw new Error('Ошибка обновления токена');
    }
  };

  const fetchWithAuth = async (url, options = {}) => {
    let accessToken = token;
    
    // Проверяем, что токен валиден перед запросом
    if (!accessToken || accessToken.split('.').length !== 3) {
      throw new Error('Невалидный JWT токен для запроса');
    }
    
    let response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.status === 401) {
      // Пробуем обновить токен
      try {
        accessToken = await refreshToken();
        response = await fetch(url, {
          ...options,
          headers: {
            ...(options.headers || {}),
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        
        // Если после обновления токена снова получаем 401, делаем logout
        if (response.status === 401) {
          logout();
          throw new Error('Требуется авторизация');
        }
      } catch (refreshError) {
        // Если не удалось обновить токен, делаем logout
        logout();
        throw new Error('Требуется авторизация');
      }
    }

    return response;
  };

  const getUserInfo = async (accessToken) => {
    try {
      const response = await safeFetch(`${API_URL}${currentLang}/user`, accessToken);
      const data = await response.json();

      if (response.ok && data.success) {
        // Обновляем язык пользователя, если он изменился
        if (data.lang && data.lang !== currentLang) {
          setCurrentLang(data.lang);
          localStorage.setItem('userLang', data.lang);
        }
        
        return {
          id: data.id,
          name: data.name,
          email: data.email,
          lang: data.lang,
          ref: data.ref,
          role: data.role === 'admin' ? 'admin' : 'user',
        };
      } else {
        logout();
        throw new Error(data.message || 'Ошибка получения информации о пользователе');
      }
    } catch (error) {
      logout();
      throw new Error(error.message || 'Ошибка сети при получении информации о пользователе');
    }
  };

  const login = async (email, password) => {
    try {
      const csrfToken = await getCsrfToken();
      const response = await fetch(`${API_URL}${currentLang}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

          if (response.ok && data.success && data.access_token) {
        // Проверяем, что полученный токен валиден
        if (data.access_token.split('.').length !== 3) {
          return { success: false, error: 'Получен невалидный JWT токен от сервера' };
        }
        
        localStorage.setItem('token', data.access_token);
        setToken(data.access_token);
        
        // Получаем информацию о пользователе
        try {
          const userInfo = await getUserInfo(data.access_token);
          setUser(userInfo);
        } catch (error) {
          // Если не удалось получить информацию, используем данные из JWT токена
          try {
            const payload = JSON.parse(atob(data.access_token.split('.')[1]));
            setUser({ 
              email: payload.email || email,
              role: payload.role === 'admin' ? 'admin' : 'user'
            });
          } catch (jwtError) {
            // Если не удалось декодировать JWT, используем email из формы
            setUser({ 
              email,
              role: 'user'
            });
          }
        }
        
        return { success: true };
    } else {
        return { success: false, error: data.message || 'Ошибка авторизации' };
      }
    } catch (error) {
      return { success: false, error: error.message || 'Ошибка сети' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const clearInvalidToken = () => {
    const storedToken = localStorage.getItem('token');
    if (storedToken && storedToken.split('.').length !== 3) {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const safeFetch = async (url, token, options = {}) => {
    // Проверяем, что токен валиден
    if (!token || token.split('.').length !== 3) {
      throw new Error('Невалидный JWT токен');
    }
    
    let response = await fetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });
    
    // Если получили 401, пробуем обновить токен
    if (response.status === 401) {
      try {
        const newToken = await refreshToken();
        response = await fetch(url, {
          ...options,
          headers: {
            ...(options.headers || {}),
            'Authorization': `Bearer ${newToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        
        // Если после обновления токена снова получаем 401, делаем logout
        if (response.status === 401) {
          logout();
          throw new Error('Требуется авторизация');
        }
      } catch (refreshError) {
        // Если не удалось обновить токен, делаем logout
        logout();
        throw new Error('Требуется авторизация');
      }
    }
    
    return response;
  };

  const changeLanguage = (newLang) => {
    if (['en', 'ru'].includes(newLang)) {
      setCurrentLang(newLang);
      localStorage.setItem('userLang', newLang);
    }
  };

  const isTokenValid = () => {
    if (!token) return false;
    try {
      // Проверяем, что токен имеет правильный формат (3 части, разделенные точками)
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }
      
      const payload = JSON.parse(atob(parts[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch (error) {
      return false;
    }
  };

  const isAuthenticated = !!token && !!user && !loading && isTokenValid();

  const value = {
    user,
    token,
    loading,
    currentLang,
    API_URL,
    csrfToken,
    login,
    logout,
    changeLanguage,
    getCsrfToken,
    isAuthenticated,
    fetchWithAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 